import { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { Copy, Check, AlertCircle, Link, FileText, QrCode, Barcode, ShieldCheck } from 'lucide-react';
import { quickCopyService } from '../services';
import api from '../lib/api';
import type { QuickCopy } from '../types/quickCopy';

function QuickCopyView() {
  const { publicKey } = useParams<{ publicKey: string }>();
  const [quickCopy, setQuickCopy] = useState<QuickCopy | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  const [qrCodeUrl, setQrCodeUrl] = useState<string | null>(null);

  useEffect(() => {
    const fetchQuickCopy = async () => {
      try {
        if (!publicKey) {
          setError('Chave pública não fornecida');
          setLoading(false);
          return;
        }
        const data = await quickCopyService.getQuickCopyByPublicKey(publicKey);
        setQuickCopy(data);

        if (data.type === 'pix') {
          try {
            const res = await api.get<{ url: string }>('/create-qr-code/', {
              params: { data: data.value, size: '300x300' },
            });
            setQrCodeUrl(res.data.url);
          } catch {
            // silently ignore; QR section will not render
          }
        }

        setLoading(false);
      } catch (err: any) {
        setError(err.message || 'Erro ao carregar o conteúdo');
        setLoading(false);
      }
    };
    fetchQuickCopy();
  }, [publicKey]);

  const copyToClipboard = () => {
    if (quickCopy?.value) {
      navigator.clipboard.writeText(quickCopy.value);
      setCopied(true);
      setTimeout(() => setCopied(false), 2500);
    }
  };

  const getTypeConfig = (type: string) => {
    switch (type) {
      case 'pix':
        return { label: 'Chave PIX', icon: <QrCode className="w-4 h-4" />, badge: 'PIX' };
      case 'barcode':
        return { label: 'Código de Barras', icon: <Barcode className="w-4 h-4" />, badge: 'Boleto' };
      case 'url':
        return { label: 'Link', icon: <Link className="w-4 h-4" />, badge: 'URL' };
      default:
        return { label: 'Texto', icon: <FileText className="w-4 h-4" />, badge: 'Texto' };
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-green-50 to-emerald-100">
        <div className="flex flex-col items-center gap-4">
          <div className="w-10 h-10 border-2 border-green-200 border-t-green-600 rounded-full animate-spin" />
          <p className="text-sm text-green-700 tracking-wide">Carregando...</p>
        </div>
      </div>
    );
  }

  if (error || !quickCopy) {
    return (
      <div className="min-h-screen flex flex-col bg-gradient-to-br from-green-50 to-emerald-100">
        <div className="flex-1 flex items-center justify-center p-4">
          <div className="bg-white border border-red-100 rounded-2xl shadow-sm p-8 max-w-sm w-full text-center">
            <div className="mx-auto w-12 h-12 rounded-full bg-red-50 flex items-center justify-center mb-4">
              <AlertCircle className="w-6 h-6 text-red-500" />
            </div>
            <h3 className="text-base font-semibold text-gray-800 mb-1">Conteúdo não encontrado</h3>
            <p className="text-sm text-gray-500">
              {error || 'O conteúdo solicitado não está disponível ou foi removido.'}
            </p>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  const typeConfig = getTypeConfig(quickCopy.type);
  const isPix = quickCopy.type === 'pix';

  return (
    <div className="min-h-screen flex flex-col bg-gradient-to-br from-green-50 to-emerald-100">
      {/* Top accent bar */}
      <div className="h-1.5 bg-gradient-to-r from-green-500 to-emerald-500" />

      <div className="flex-1 flex flex-col items-center justify-center p-4 py-10">
        <div className="w-full max-w-md">
          {/* Header */}
          <div className="text-center mb-7">
            <div className="inline-flex items-center gap-2 bg-green-100 text-green-700 text-xs font-semibold px-3 py-1.5 rounded-full mb-4 tracking-wide uppercase">
              {typeConfig.icon}
              {typeConfig.badge}
            </div>
            <h1 className="text-2xl font-bold text-gray-900 tracking-tight">
              {quickCopy.label || typeConfig.label}
            </h1>
            <p className="text-xs text-gray-400 mt-1.5">
              {isPix
                ? 'Escaneie o QR code ou copie a chave abaixo'
                : 'Copie o conteúdo abaixo com um clique'}
            </p>
          </div>

          {/* Card */}
          <div className="bg-white rounded-2xl shadow-md border border-green-100 overflow-hidden">
            {/* Value display */}
            <div className="px-6 pt-5 pb-4">
              <p className="text-[11px] font-semibold text-gray-400 uppercase tracking-widest mb-2">
                {isPix ? 'Chave copia e cola' : 'Conteúdo'}
              </p>
              <div className="bg-gray-50 border border-gray-100 rounded-xl p-4">
                <pre className="whitespace-pre-wrap break-all text-sm text-gray-700 font-mono leading-relaxed">
                  {quickCopy.value}
                </pre>
              </div>
            </div>

            {/* Copy button */}
            <div className="px-6 pb-5">
              <button
                onClick={copyToClipboard}
                className={`w-full flex items-center justify-center gap-2.5 rounded-xl px-5 py-3.5 text-sm font-semibold transition-all duration-200 shadow-sm ${
                  copied
                    ? 'bg-emerald-50 text-emerald-700 border-2 border-emerald-300 shadow-none'
                    : 'bg-gradient-to-r from-green-500 to-emerald-500 text-white hover:from-green-600 hover:to-emerald-600 active:scale-[0.98]'
                }`}
              >
                {copied ? (
                  <>
                    <Check className="w-4 h-4" />
                    Copiado com sucesso!
                  </>
                ) : (
                  <>
                    <Copy className="w-4 h-4" />
                    {isPix ? 'Copiar chave PIX' : 'Copiar conteúdo'}
                  </>
                )}
              </button>
            </div>

            {/* QR Code for PIX */}
            {isPix && qrCodeUrl && (
              <div className="flex flex-col items-center px-8 pt-4 pb-7 bg-gradient-to-b from-white to-green-50 border-t border-green-100">
                <p className="text-[11px] font-semibold text-gray-400 uppercase tracking-widest mb-4">
                  QR Code PIX
                </p>
                <div className="bg-white p-3 border-2 border-green-200 rounded-2xl shadow-sm">
                  <img
                    src={qrCodeUrl}
                    alt="QR Code PIX"
                    width={196}
                    height={196}
                    className="block rounded"
                  />
                </div>
                <p className="text-xs text-gray-400 mt-3 flex items-center gap-1.5">
                  <ShieldCheck className="w-3.5 h-3.5 text-green-500" />
                  Aponte a câmera do seu banco para pagar
                </p>
              </div>
            )}
          </div>

          {/* Trust badge */}
          <div className="flex items-center justify-center gap-1.5 mt-5 text-xs text-gray-400">
            <ShieldCheck className="w-3.5 h-3.5 text-green-500" />
            Transação segura e criptografada
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
}

function Footer() {
  return (
    <footer className="py-5 px-4 border-t border-green-100 bg-white/60 backdrop-blur-sm">
      <div className="max-w-md mx-auto flex flex-col items-center gap-1">
        <p className="text-xs text-gray-500 text-center">
          Esta página faz parte da plataforma{' '}
          <span className="font-semibold text-green-700">MAHIA</span>
        </p>
        <p className="text-[11px] text-gray-400">&copy; {new Date().getFullYear()} MAHIA. Todos os direitos reservados.</p>
      </div>
    </footer>
  );
}

export default QuickCopyView;
